import { AxiosResponse } from 'axios';
import api from '../api';
import { IExamReportCardResponse } from '../../interface/reportCard.interface';
import {
  IMyStudentIdentityResponse,
  IStudentExamDetailResponse,
} from '../../interface/studentPortalExam.interface';
import { IParentExamListResponse } from '../../interface/reportCard.interface';
import { REPORT_CARD_PDF_TIMEOUT_MS } from '../../utility/downloadReportCard';

// Student-portal exam endpoints. The base URL already includes `/v2`.
//
// These routes are SHARED with guardians: a parent may log in and hand the device
// to the child, so both audiences call exactly the same endpoints with an explicit
// studentId. The server accepts either the Student View Exam or Parent View Exam
// privilege and then resolves whether the caller may act for that student.

/** Resolves the logged-in student's own id. Called once on load. */
const fetchMyStudentIdentity = async (): Promise<AxiosResponse<IMyStudentIdentityResponse>> => {
  return api.get<IMyStudentIdentityResponse>('/me/student');
};

/** The student's exams. Same payload a guardian receives for the same student. */
const fetchStudentExams = async (
  studentId: string,
): Promise<AxiosResponse<IParentExamListResponse>> => {
  return api.get<IParentExamListResponse>(`/student/exam/${studentId}`);
};

/** One exam's subjects. Scores are null until the exam is released. */
const fetchStudentExamDetail = async (
  studentId: string,
  examId: string,
): Promise<AxiosResponse<IStudentExamDetailResponse>> => {
  return api.get<IStudentExamDetailResponse>(`/student/exam/${studentId}/${examId}`);
};

const fetchStudentReportCard = async (
  studentId: string,
  examId: string,
): Promise<AxiosResponse<IExamReportCardResponse>> => {
  return api.get<IExamReportCardResponse>(`/student/exam/${studentId}/${examId}/report-card`);
};

/**
 * PDF download. Returns the raw response so the caller can read the
 * server-provided filename off Content-Disposition.
 */
const downloadStudentReportCard = async (
  studentId: string,
  examId: string,
): Promise<AxiosResponse<Blob>> => {
  return api.get<Blob>(`/student/exam/${studentId}/${examId}/report-card/pdf`, {
    responseType: 'blob',
    // Server-side puppeteer render — needs more than the instance's 10s default.
    timeout: REPORT_CARD_PDF_TIMEOUT_MS,
  });
};

export const studentPortalService = {
  fetchMyStudentIdentity,
  fetchStudentExams,
  fetchStudentExamDetail,
  fetchStudentReportCard,
  downloadStudentReportCard,
};
